import type { ComponentType } from 'react';

import type { DynamicOptions, LoaderComponent } from 'next/dynamic';
import dynamic from 'next/dynamic';

import { ChunkSkewErrorBoundary } from 'components/ChunkSkewErrorBoundary';

import { importWithRetry } from './importWithRetry';

export type DynamicComponentWithPreload<P> = ComponentType<P> & {
    /**
     * Starts downloading the chunk ahead of the first render (e.g. on hover) so opening it doesn't
     * wait. Safe to call repeatedly; a failed warm-up is dropped and retried by the next call or render.
     */
    preload: () => void;
    /**
     * Whether rendering it failed to load the chunk, retries and all. `React.lazy` keeps that failure, so rendering it
     * again never asks for the chunk again, even once a preload has fetched it; only a new `dynamicWithRetry` does.
     */
    hasFailed: () => boolean;
};

export function dynamicWithRetry<P extends object>(
    importFn: () => LoaderComponent<P>,
    options: DynamicOptions<P> = {},
): DynamicComponentWithPreload<P> {
    let failed = false;
    const loader = () =>
        importWithRetry(importFn).catch((error: unknown) => {
            failed = true;
            throw error;
        });

    const DynamicComponent =
        options.loading !== undefined
            ? dynamic<P>(loader, {
                  loading: options.loading,
                  ssr: options.ssr ?? false,
              })
            : dynamic<P>(loader, {
                  ssr: options.ssr ?? false,
              });

    // A chunk from a newer deploy can load successfully but throw at render
    // time when it references exports missing from the older shared modules
    // already in memory. importWithRetry only covers chunks that fail to
    // load, so wrap every lazy component in the skew boundary to recover.
    function DynamicWithSkewRecovery(props: P) {
        return (
            <ChunkSkewErrorBoundary>
                <DynamicComponent {...props} />
            </ChunkSkewErrorBoundary>
        );
    }

    // Plain importFn, not importWithRetry: a warm-up must never trigger importWithRetry's
    // reload-on-exhaustion. Rendering still goes through the retrying loader.
    let preloading: Promise<unknown> | undefined;
    DynamicWithSkewRecovery.preload = (): void => {
        preloading ??= Promise.resolve(importFn()).catch((error: unknown) => {
            preloading = undefined;
            console.warn('[dynamicWithRetry] preload failed; the chunk loads again on render', error);
        });
    };

    DynamicWithSkewRecovery.hasFailed = (): boolean => failed;

    return DynamicWithSkewRecovery;
}
